import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Book, BookmarkSimple, ArrowLeft, Eye, Heart } from '@phosphor-icons/react';
import GlobalHeader from '../components/GlobalHeader';

export const Bookmarks = () => {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/me/bookmarks`,
          { withCredentials: true }
        );
        setArticles(res.data);
      } catch (error) {
        console.error('Error fetching bookmarks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookmarks();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <BookmarkSimple size={32} weight="duotone" className="text-primary" />
            <h2 className="text-4xl md:text-5xl tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              Your Bookmarks
            </h2>
          </div>
          <p className="text-muted-foreground mb-12">{articles.length} saved article{articles.length !== 1 ? 's' : ''}</p>

          {articles.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-16 text-center" data-testid="no-bookmarks">
              <BookmarkSimple size={48} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
              <p className="text-lg mb-2">No bookmarks yet</p>
              <p className="text-muted-foreground mb-6">Save articles you love to read them later</p>
              <Link
                to="/browse"
                data-testid="browse-articles-link"
                className="inline-block bg-primary text-primary-foreground rounded-full py-3 px-8 font-semibold hover:opacity-90 transition-opacity"
              >
                Browse Articles
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.article_id}
                  to={`/article/${article.article_id}`}
                  data-testid={`bookmark-${article.article_id}`}
                  className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary transition-colors"
                >
                  {article.featured_image && (
                    <div className="aspect-video bg-muted">
                      <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    <span className="text-xs font-semibold uppercase tracking-widest text-primary mb-3 block">{article.category}</span>
                    <h4 className="text-xl mb-3 line-clamp-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                      {article.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{article.meta_description}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{article.author_name}</span>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><Eye size={14} />{article.views}</span>
                        <span className="flex items-center gap-1"><Heart size={14} />{article.likes}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Bookmarks;
